<template>
  <div class="rm-screen">
    <div v-if="!selectedCompanies.length" class="rm-empty">
      <v-icon size="40" color="#8a817a">mdi-domain</v-icon>
      {{ $t("no_companies_selected") }}
    </div>
    <div v-else-if="isLoading" class="rm-empty">
      <v-progress-circular indeterminate color="#c2410c"></v-progress-circular>
    </div>
    <div v-else-if="!sections.length" class="rm-empty">
      <v-icon v-if="!filters.search" size="40" color="#8a817a">mdi-food-off</v-icon>
      {{ emptyTitle }}
    </div>

    <template v-else>
      <div class="rm-stats rm-stats--pair">
        <div class="rm-stat rm-stat--inverse">
          <span class="rm-stat__label">{{ $t("ordered") }}</span>
          <span class="rm-stat__value rm-num">{{ totals.ordered }}</span>
        </div>
        <div class="rm-stat">
          <span class="rm-stat__label">{{ $t("taken") }}</span>
          <span class="rm-stat__value rm-num">{{ totals.taken }}</span>
        </div>
      </div>

      <section v-for="section in sections" :key="section.key" class="rm-card rm-mcard">
        <button
          type="button"
          class="rm-mcard__head"
          :aria-expanded="String(isOpen(section.key))"
          @click="toggleCompany(section.key)"
        >
          <span class="rm-mcard__title">
            <span class="rm-mcard__name">{{ section.name }}</span>
            <span class="rm-muted">{{ section.detailLine || section.summary }}</span>
          </span>
          <span class="rm-mcard__value rm-num">
            {{ section.totals.totalPayed }}<span class="rm-mcard__of"> / {{ section.totals.total }}</span>
          </span>
          <v-icon size="18" color="#8a817a" class="rm-mcard__chevron" :class="{ 'is-open': isOpen(section.key) }">
            mdi-chevron-down
          </v-icon>
        </button>
        <div
          class="rm-progress rm-mcard__progress"
          :class="{ 'rm-progress--complete': section.complete }"
          role="progressbar"
          :aria-valuenow="section.totals.totalPayed"
          aria-valuemin="0"
          :aria-valuemax="section.totals.total"
        >
          <div class="rm-progress__fill" :style="{ width: `${section.percent}%` }"></div>
        </div>

        <ul v-if="isOpen(section.key)" class="rm-list rm-mcard__list">
          <li v-for="row in section.rows" :key="row.key" class="rm-mrow">
            <div class="rm-mrow__main">
              <span class="rm-mrow__text">
                <span class="rm-mrow__name">{{ row.name }}</span>
                <span v-if="showCombination && row.combination" class="rm-muted mp-combo">{{ row.combination }}</span>
              </span>
              <span class="rm-mrow__value rm-num" :class="{ 'is-complete': isComplete(row.totalPayed, row.total) }">
                <v-icon v-if="isComplete(row.totalPayed, row.total)" size="16" color="#15803d">mdi-check-circle</v-icon>
                {{ row.totalPayed }}<span class="rm-mrow__of"> / {{ row.total }}</span>
              </span>
            </div>
            <div v-if="rowShifts(row).length" class="rm-mrow__shifts">
              <span v-for="shift in rowShifts(row)" :key="shift.id" class="rm-mrow__shift rm-num">
                <report-shift-tag :shift="shift.id"></report-shift-tag>
                {{ shift.taken }} / {{ shift.ordered }}
              </span>
            </div>
          </li>
          <li v-if="isSingleDay && !packing" class="rm-mrow mp-actions">
            <button type="button" class="rm-btn rm-btn--sm" @click="openAddDialog(section.company)">
              <v-icon size="18" color="#1c1917">mdi-plus</v-icon>
              {{ $t("report_mobile.add_meal") }}
            </button>
          </li>
        </ul>
      </section>
    </template>

    <report-meals-packages-add-meal
      v-if="selectedCompany"
      v-model="addMealDialog"
      :company="selectedCompany"
      :date="filters.dates.from"
      @refreshMeal="loadMealsForRestaurantCompanies"
    ></report-meals-packages-add-meal>
  </div>
</template>

<script>
import ReportMealsTabPackages from "@/components/report/more-restaurants/tabs/ReportMealsTabPackages.vue";
import hasShiftBinding from "../../../shared/hasShiftBinding";
import hasShiftSubtitle from "../../../shared/hasShiftSubtitle";
import hasMorePackageSections from "../../../shared/more/hasMorePackageSections";
import ReportShiftTag from "../../../shared/ReportShiftTag.vue";
import { percentOf } from "../../../shared/takenMath";
import "../reportMobileMealCards.scss";

/**
 * Mobilni "Pakovanje" i "Preuzeti obroci" (firme koje hrani restoran): po firmi preuzeto / poručeno, a po obroku
 * (i sadržaju paketa) i po smeni. Za jedan dan se u Preuzetim obrocima uz svaku firmu mogu dodati obroci. Firme,
 * smene, period i pretraga su u zaglavlju. Nasleđuje učitavanje, štampu i Excel iz starog taba; prop `packing`
 * bira Pakovanje (paket sa različitim izborima je poseban red) umesto Preuzetih obroka.
 */
export default {
  name: "MoreMobilePackages",
  extends: ReportMealsTabPackages,
  mixins: [hasShiftBinding, hasShiftSubtitle, hasMorePackageSections],
  components: { ReportShiftTag },
  data: () => ({
    closed: {},
  }),
  computed: {
    totals() {
      return this.sections.reduce(
        (sums, section) => ({ ordered: sums.ordered + section.totals.total, taken: sums.taken + section.totals.totalPayed }),
        { ordered: 0, taken: 0 }
      );
    },
  },
  methods: {
    isOpen(key) {
      return Boolean(this.filters.search) || !this.closed[key];
    },
    toggleCompany(key) {
      this.closed = { ...this.closed, [key]: !this.closed[key] };
    },
    isComplete(taken, total) {
      return total > 0 && taken >= total;
    },
    /** Smene u kojima obrok ima narudžbe (samo izabrane). */
    rowShifts(row) {
      return this.visibleShifts
        .map((shift) => ({ id: shift.id, ordered: row[shift.key].ordered, taken: row[shift.key].taken }))
        .filter((shift) => shift.ordered > 0);
    },
  },
};
</script>

<style lang="scss">
.rm-theme .mp-combo {
  display: block;
  font-size: 13px;
  overflow-wrap: anywhere;
}

.rm-theme .mp-actions {
  display: flex;
  justify-content: flex-start;
}
</style>
